import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, LayoutDashboard, FilePlus2, FileText, Users, Building2, Menu, X, FileSpreadsheet, UserSquare2, StickyNote, Settings, PencilRuler, Inbox, Ruler, DoorOpen } from "lucide-react";

function NavItem({ to, icon: Icon, label, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-l-2 transition-colors
        ${isActive
          ? "border-black bg-zinc-100 text-zinc-900"
          : "border-transparent text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"}`
      }
    >
      <Icon size={16} strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const close = () => setOpen(false);

  const SidebarContent = ({ onItemClick }) => (
    <>
      <div className="px-5 py-5 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/brand/mak-logo.png" alt="MAK" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="font-semibold text-zinc-900 text-sm tracking-wide">MAK</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Konfigurator</div>
          </div>
        </div>
        <button onClick={close} className="md:hidden text-zinc-500 p-1" data-testid="close-menu-button">
          <X size={20}/>
        </button>
      </div>
      <nav className="flex-1 py-4 flex flex-col gap-0.5 overflow-y-auto">
        <div className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
          Arbeitsbereich
        </div>
        <NavItem to="/" icon={LayoutDashboard} label="Übersicht" end onClick={onItemClick} />
        <NavItem to="/quote/new" icon={FilePlus2} label="Neues Angebot" onClick={onItemClick} />
        <NavItem to="/planner" icon={PencilRuler} label="Zaunplaner" onClick={onItemClick} />
        <NavItem to="/balkon" icon={Ruler} label="Balkon-Werkzeichnung" onClick={onItemClick} />
        <NavItem to="/schiebetor" icon={DoorOpen} label="Schiebetor" onClick={onItemClick} />
        <NavItem to="/quotes" icon={FileText} label="Angebote" onClick={onItemClick} />
        <NavItem to="/customers" icon={UserSquare2} label="Kunden" onClick={onItemClick} />
        {!isAdmin && (
          <NavItem to="/meine-firma" icon={Building2} label="Meine Firma" onClick={onItemClick} />
        )}
        {isAdmin && (
          <>
            <div className="px-4 pt-6 pb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
              Administration
            </div>
            <NavItem to="/inquiries" icon={Inbox} label="Anfragen" onClick={onItemClick} />
            <NavItem to="/admin/users" icon={Users} label="Benutzer" onClick={onItemClick} />
            <NavItem to="/admin/dealers" icon={Building2} label="Händler" onClick={onItemClick} />
            <NavItem to="/admin/pricelist" icon={FileSpreadsheet} label="Preisliste" onClick={onItemClick} />
            <NavItem to="/admin/note-templates" icon={StickyNote} label="Hinweise" onClick={onItemClick} />
            <NavItem to="/admin/settings" icon={Settings} label="Einstellungen" onClick={onItemClick} />
          </>
        )}
      </nav>
      <div className="border-t border-zinc-200 p-4">
        <div className="text-xs text-zinc-500 mb-1">Angemeldet als</div>
        <div className="text-sm font-medium text-zinc-900 truncate" data-testid="current-user-name">
          {user?.name}
        </div>
        <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
        <div className="text-[10px] uppercase tracking-widest mt-1 inline-block px-1.5 py-0.5 bg-zinc-900 text-white">
          {user?.role}
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-medium text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 hover:bg-white transition-colors"
        >
          <LogOut size={13} /> Abmelden
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-layout">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/brand/mak-logo.png" alt="MAK" className="h-8 w-8 object-contain" />
          <div className="font-semibold tracking-wide text-sm">MAK</div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="open-menu-button" className="text-zinc-700 p-1">
          <Menu size={22}/>
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 border-r border-zinc-200 bg-zinc-50/40 flex-col sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={close}>
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
